import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

/**
 * FrostMenuManagerService
 * 
 * Uygulamadaki tüm Frost menülerinin açık/kapalı durumlarını yöneten merkezi servis.
 * Bu servis sayesinde dock, overlay ve diğer bileşenler açık menü olup olmadığını
 * kolayca kontrol edebilir.
 */
@Injectable({
    providedIn: 'root'
})
export class FrostMenuManagerService {
    
    // Açık menüleri izleyen BehaviorSubject
    private _openMenusSubject = new BehaviorSubject<Set<string>>(new Set<string>());
    
    // Dışarıya açık menü durumları için Observable'lar
    readonly openMenus$: Observable<Set<string>> = this._openMenusSubject.asObservable();
    
    // Herhangi bir menü açık mı Observable'ı
    readonly hasOpenMenu$: Observable<boolean> = this._openMenusSubject.pipe(
        map(menus => menus.size > 0),
        distinctUntilChanged()
    );
    
    // Açık menü sayısı Observable'ı
    readonly openMenuCount$: Observable<number> = this._openMenusSubject.pipe(
        map(menus => menus.size),
        distinctUntilChanged()
    );
    
    constructor() {
        console.log('FrostMenuManagerService initialized');
    }
    
    /**
     * Bir menünün açıldığını bildirir
     * @param menuId Açılan menünün benzersiz tanımlayıcısı
     */
    registerOpenMenu(menuId: string): void {
        if (!menuId) return;
        
        const currentMenus = this._openMenusSubject.value;
        if (!currentMenus.has(menuId)) {
            const newMenus = new Set(currentMenus);
            newMenus.add(menuId);
            this._openMenusSubject.next(newMenus);
            console.log(`Menu opened: ${menuId}, total open menus: ${newMenus.size}`);
        }
    }
    
    /**
     * Bir menünün kapandığını bildirir
     * @param menuId Kapanan menünün benzersiz tanımlayıcısı
     */
    registerClosedMenu(menuId: string): void {
        if (!menuId) return;
        
        const currentMenus = this._openMenusSubject.value;
        if (currentMenus.has(menuId)) {
            const newMenus = new Set(currentMenus);
            newMenus.delete(menuId);
            this._openMenusSubject.next(newMenus);
            console.log(`Menu closed: ${menuId}, total open menus: ${newMenus.size}`);
        }
    }
    
    /**
     * Tüm menüleri kapatır
     */
    closeAllMenus(): void {
        if (this._openMenusSubject.value.size > 0) {
            this._openMenusSubject.next(new Set());
            console.log('All menus closed');
        }
    }
    
    /**
     * Herhangi bir menünün açık olup olmadığını kontrol eder
     * @returns Açık menü varsa true, yoksa false
     */
    hasOpenMenu(): boolean {
        return this._openMenusSubject.value.size > 0;
    }
    
    /**
     * Belirli bir menünün açık olup olmadığını kontrol eder
     * @param menuId Kontrol edilecek menünün ID'si
     * @returns Menü açıksa true, kapalıysa false
     */
    isMenuOpen(menuId: string): boolean {
        return this._openMenusSubject.value.has(menuId);
    }
    
    /**
     * Açık menülerin listesini döndürür
     * @returns Açık menü ID'lerinin dizisi
     */
    getOpenMenuIds(): string[] {
        return Array.from(this._openMenusSubject.value);
    }
    
    /**
     * Açık menü sayısını döndürür
     * @returns Açık menü sayısı
     */
    getOpenMenuCount(): number {
        return this._openMenusSubject.value.size;
    }
} 